import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, FileText, Search, Download, Filter, ClipboardList, BarChart3, ClipboardCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import { PrOverdueBanner } from '@/components/pr/PrOverdueBanner';
import { OverReceiptSetting } from '@/components/po/OverReceiptSetting';
import { PoAlertsBanner } from '@/components/po/PoAlertsBanner';
import { fmtQ } from '@/components/po/PoLinePicker';

// Never add pcs and mtr together — one figure per unit when a PO mixes units.
const unitText = (p, k) => {
  const u = p.totals_by_unit || [];
  return u.length > 1 ? u.map((x) => `${fmtQ(x[k])} ${x.unit}`).join(' · ') : fmtQ(u[0]?.[k] || 0);
};

const STATUS_COLORS = {
  DRAFT: 'bg-slate-100 text-slate-700 border-slate-300',
  ISSUED: 'bg-blue-100 text-blue-700 border-blue-300',
  PARTIAL_RECEIVED: 'bg-amber-100 text-amber-800 border-amber-300',
  COMPLETED: 'bg-green-100 text-green-700 border-green-300',
  CANCELLED: 'bg-red-100 text-red-700 border-red-300',
};

const POList = () => {
  const navigate = useNavigate();
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ status: 'all', section: 'all' });
  const [sections, setSections] = useState([]);

  const fetchSections = async () => {
    try {
      const r = await apiClient.get('/purchase-orders/sections/list');
      setSections(r.data.sections || []);
    } catch (e) { /* noop */ }
  };

  const fetchPOs = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status && filters.status !== 'all') params.status = filters.status;
      if (filters.section && filters.section !== 'all') params.section_name = filters.section;
      const r = await apiClient.get('/purchase-orders', { params });
      setPos(r.data.pos || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load POs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSections(); }, []);
  useEffect(() => { fetchPOs(); }, [filters]);

  const filtered = pos.filter(p => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (p.po_number || '').toLowerCase().includes(q)
      || (p.vendor_name || '').toLowerCase().includes(q)
      || (p.vendor_code || '').toLowerCase().includes(q);
  });

  const handleExport = async () => {
    try {
      const params = {};
      if (filters.status && filters.status !== 'all') params.status = filters.status;
      if (filters.section && filters.section !== 'all') params.section_name = filters.section;
      const r = await apiClient.get('/purchase-orders/report/export', { params, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `po_report_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch (e) {
      toast.error('Export failed');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="po-list-page">
      <div className="bg-white border-b px-4 pt-10 pb-3 md:py-3 flex flex-wrap items-center gap-2 md:gap-3 sticky top-0 z-10">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}><ArrowLeft className="w-4 h-4" /></Button>
        <div className="flex-1 min-w-[200px]">
          <h1 className="text-lg font-bold text-gray-900">Purchase Orders</h1>
          <p className="text-xs text-gray-500">Manage vendor POs — create, issue and track what the sections received.</p>
        </div>
        <OverReceiptSetting />
        <Button onClick={handleExport} variant="outline" size="sm" data-testid="export-csv-btn"><Download className="w-4 h-4 mr-1" />Export</Button>
        <Button onClick={() => navigate('/po/follow-up')} variant="outline" size="sm" data-testid="po-followup-btn"><BarChart3 className="w-4 h-4 mr-1" />Pending vs Received</Button>
        <Button onClick={() => navigate('/po/grn')} variant="outline" size="sm" data-testid="po-grn-btn"><ClipboardCheck className="w-4 h-4 mr-1" />GRNs</Button>
        <Button onClick={() => navigate('/pr')} variant="outline" size="sm" data-testid="po-list-requisitions-btn"><ClipboardList className="w-4 h-4 mr-1" />Requisitions</Button>
        <Button onClick={() => navigate('/po/create')} data-testid="create-po-btn"><Plus className="w-4 h-4 mr-1" />Create PO</Button>
      </div>

      <div className="max-w-7xl mx-auto p-4 space-y-4">
        <PrOverdueBanner onShowAll={() => navigate('/pr?tab=overdue')} />
        <PoAlertsBanner />
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-wrap gap-2 items-center mb-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search PO#, vendor..." className="pl-9" data-testid="po-search" />
              </div>
              <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                <SelectTrigger className="w-[160px]" data-testid="status-filter"><Filter className="w-3 h-3 mr-1" /><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="DRAFT">Draft</SelectItem>
                  <SelectItem value="ISSUED">Issued</SelectItem>
                  <SelectItem value="PARTIAL_RECEIVED">Partial</SelectItem>
                  <SelectItem value="COMPLETED">Completed</SelectItem>
                  <SelectItem value="CANCELLED">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filters.section} onValueChange={(v) => setFilters({ ...filters, section: v })}>
                <SelectTrigger className="w-[180px]" data-testid="section-filter"><SelectValue placeholder="Section" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sections</SelectItem>
                  {sections.filter(s => s.section_name).map(s => <SelectItem key={s.section_id || s.section_name} value={s.section_name}>{s.section_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {loading ? <div className="text-center py-8 text-gray-400">Loading...</div> :
              filtered.length === 0 ? <div className="text-center py-8 text-gray-400">No POs found</div> : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>PO #</TableHead>
                      <TableHead>Vendor</TableHead>
                      <TableHead>Section</TableHead>
                      <TableHead className="text-center">Items</TableHead>
                      <TableHead className="text-right">Ordered</TableHead>
                      <TableHead className="text-right">Received</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                      <TableHead className="text-right">Total ₹</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map(p => (
                      <TableRow key={p.po_id} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/po/${p.po_id}`)} data-testid={`po-row-${p.po_number}`}>
                        <TableCell className="font-mono text-xs">{p.po_number}</TableCell>
                        <TableCell>
                          <div className="font-medium text-sm">{p.vendor_name}</div>
                          <div className="text-xs text-gray-400">{p.vendor_code}</div>
                        </TableCell>
                        <TableCell className="text-sm">{p.section_name}</TableCell>
                        <TableCell className="text-center">{p.item_count}</TableCell>
                        <TableCell className="text-right text-sm whitespace-nowrap">{unitText(p, 'ordered')}</TableCell>
                        <TableCell className="text-right text-sm whitespace-nowrap">{unitText(p, 'received')}</TableCell>
                        <TableCell className="text-right text-sm font-semibold whitespace-nowrap" data-testid={`po-row-balance-${p.po_number}`}>{unitText(p, 'balance')}</TableCell>
                        <TableCell className="text-right text-sm">{p.grand_total?.toLocaleString('en-IN')}</TableCell>
                        <TableCell><Badge className={STATUS_COLORS[p.status]} variant="outline">{p.status}</Badge></TableCell>
                        <TableCell className="text-xs text-gray-500">{p.po_date}</TableCell>
                        <TableCell><FileText className="w-4 h-4 text-gray-400" /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default POList;
